import { createAsyncThunk } from '@reduxjs/toolkit'
import axiosInstance from './axios/axiosConfig'
import { endpoints } from './axios/endpoints'
import withApiError from './axios/withApiError'

const TemplateService = {
    // `{ background: true }`: the caller shows its own loading state (the
    // library, the pipelines page's name lookup), so no full-screen loader.
    getTemplates: createAsyncThunk('template/getTemplates', async (opts) =>
        axiosInstance.get(
            endpoints.getTemplates,
            opts?.background ? { background: true } : undefined
        )
    ),
    createTemplate: createAsyncThunk(
        'template/createTemplate',
        withApiError((data) =>
            // The form shows a failure inline (and checks a timed-out save).
            axiosInstance.post(endpoints.createTemplate, data, {
                silentErrorToast: true,
            })
        )
    ),
    updateTemplate: createAsyncThunk(
        'template/updateTemplate',
        withApiError(({ templateID, payload }) =>
            // The edit form shows a failure inline.
            axiosInstance.put(endpoints.updateTemplate(templateID), payload, {
                silentErrorToast: true,
            })
        )
    ),
    getSenderEmails: createAsyncThunk(
        'template/getSenderEmails',
        withApiError(() =>
            // A list that fails to load leaves the default address.
            axiosInstance.get(endpoints.getSenderEmails, {
                silentToast: true,
                silentErrorToast: true,
            })
        )
    ),
    // Rejects with the API's body, so a page can show why (e.g. "not found").
    getTemplateData: createAsyncThunk(
        'template/getTemplateData',
        withApiError((data) =>
            // Every preview shows a failure in its dialog or page.
            axiosInstance.get(endpoints.getTemplateData(data), {
                silentErrorToast: true,
            })
        )
    ),
    // The same body as getTemplateData, for a download: the slice does not
    // handle this action, so fetching it never opens the preview dialog. The
    // page names the template in its own error message.
    getTemplateBody: createAsyncThunk(
        'template/getTemplateBody',
        withApiError((templateID) =>
            axiosInstance.get(endpoints.getTemplateData(templateID), {
                silentErrorToast: true,
            })
        )
    ),
    getTemplate: createAsyncThunk(
        'template/getTemplate',
        withApiError((templateID) =>
            axiosInstance.get(endpoints.getTemplate(templateID))
        )
    ),
    // The same row for the library's WhatsApp preview (header, footer,
    // buttons and the header media's signed link). A thunk of its own so the
    // edit form's getTemplate is untouched; a failure leaves the preview
    // with the body alone, so it is silent.
    getTemplateDetails: createAsyncThunk(
        'template/getTemplateDetails',
        withApiError((templateID) =>
            axiosInstance.get(endpoints.getTemplate(templateID), {
                silentErrorToast: true,
            })
        )
    ),
    // Amazon Pinpoint's templates of one channel. The library shows a short
    // notice of its own when this fails, so there is no toast.
    getPinpointTemplates: createAsyncThunk(
        'template/getPinpointTemplates',
        withApiError((commType) =>
            axiosInstance.get(endpoints.getPinpointTemplates(commType), {
                silentErrorToast: true,
                background: true,
            })
        )
    ),
    // One Pinpoint SMS template ({Body, LastModifiedDate, ...}). With
    // `preview` the slice opens the preview dialog on it; without, the
    // caller uses the body itself (a download).
    getPinpointTemplate: createAsyncThunk(
        'template/getPinpointTemplate',
        withApiError(({ templateName }) =>
            axiosInstance.get(
                endpoints.getPinpointTemplate('sms', templateName),
                { silentErrorToast: true }
            )
        )
    ),
    // A WhatsApp header's image, video or document, stored before the
    // template is created; answers its `s3_path`. The form says why an
    // upload failed, so neither the success nor the failure is toasted.
    uploadWhatsAppMedia: createAsyncThunk(
        'template/uploadWhatsAppMedia',
        withApiError((file) => {
            const form = new FormData()
            form.append('file', file)
            form.append('commtype', 'whatsapp')
            return axiosInstance.post(endpoints.uploadFile, form, {
                silentToast: true,
                silentErrorToast: true,
            })
        })
    ),
    // How likely WhatsApp is to approve a Utility body: {percentage,
    // category, reasons, recommendations}. The body travels in the JSON body
    // (services#12508): as a query parameter a 1,024-character Hindi body is
    // about 9 KB of address once percent-encoded, past the proxy's limit.
    getApprovalProbability: createAsyncThunk(
        'template/getApprovalProbability',
        withApiError((message) =>
            // A read sent as POST: the check shows "Checking…" on its own
            // disabled button, so it does not cover the screen.
            axiosInstance.post(
                endpoints.approvalProbability,
                { message },
                { silentToast: true, silentErrorToast: true, background: true }
            )
        )
    ),
    // A rewrite of the body that follows the Utility rules: {revised_message,
    // changes_made, approval_category, expected_improvement}.
    getRevisedMessage: createAsyncThunk(
        'template/getRevisedMessage',
        withApiError((payload) =>
            // A model call that writes nothing; the panel shows "Getting the
            // recommendation…" on its own disabled button.
            axiosInstance.post(endpoints.revisedMessage, payload, {
                silentToast: true,
                silentErrorToast: true,
                background: true,
            })
        )
    ),
    getTemplateDataJson: createAsyncThunk(
        'template/getTemplateDataJson',
        async (data) => axiosInstance.get(endpoints.getTemplateDataJson(data))
    ),
    getApproverTypes: createAsyncThunk('template/getApproverTypes', async () =>
        axiosInstance.get(endpoints.getApproverTypes)
    ),
    sendForApproval: createAsyncThunk(
        'template/sendForApproval',
        withApiError((data) =>
            axiosInstance.post(endpoints.sendForApproval(data))
        )
    ),

    saveTemplateData: createAsyncThunk(
        'template/saveTemplateData',
        async (data) => {
            let payload = {
                template_content: data.template_content,
                design_json: data.design_json,
            }
            return axiosInstance.put(
                endpoints.saveTemplateData(data.template_id),
                payload
            )
        }
    ),
    sendTestCommunication: createAsyncThunk(
        'template/sendTestCommunication',
        withApiError((data) =>
            axiosInstance.post(endpoints.sendTestCommunication, data)
        )
    ),
    // Delivery outcome of a test send: the send API only queues it.
    getTestCommunicationStatus: createAsyncThunk(
        'template/getTestCommunicationStatus',
        withApiError((executionId) =>
            axiosInstance.get(
                endpoints.getTestCommunicationStatus(executionId),
                // Polled every few seconds; failures are shown on the page.
                { silentToast: true }
            )
        )
    ),
    deleteTemplate: createAsyncThunk(
        'template/deleteTemplate',
        withApiError((templateID) =>
            // The library names the template in its own message.
            axiosInstance.put(endpoints.deleteTemplate(templateID), null, {
                silentErrorToast: true,
                silentToast: true,
            })
        )
    ),
    approveTemplate: createAsyncThunk(
        'template/approveTemplate',
        withApiError((data) =>
            axiosInstance.put(endpoints.approveTemplate(data))
        )
    ),
}

export default TemplateService
